import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const WhyUs: React.FC = () => {
  const { t } = useTranslation();

  const items = [
    {
      id: "curated",
      title: t("whyUs.items.curated.title"),
      text: t("whyUs.items.curated.text"),
    },
    {
      id: "tailored",
      title: t("whyUs.items.tailored.title"),
      text: t("whyUs.items.tailored.text"),
    },
    {
      id: "local",
      title: t("whyUs.items.local.title"),
      text: t("whyUs.items.local.text"),
    },
  ];

  return (
    <section className="relative py-28 sm:py-36 lg:py-44 px-6 sm:px-8 lg:px-12 overflow-hidden bg-[#F8F5EF]">
      <div className="max-w-7xl mx-auto">
        {/* Asymmetric editorial heading */}
        <motion.header
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-20 sm:mb-24"
        >
          <div className="lg:col-span-8">
            <div className="flex items-center gap-5 mb-8 max-w-xl">
              <span className="font-oswald text-[11px] uppercase tracking-[0.34em] text-[#a26850] whitespace-nowrap">
                {t("whyUs.eyebrow")}
              </span>
              <span className="h-px flex-1 bg-stone-300/80" aria-hidden="true" />
            </div>

            <h2 className="font-oswald font-medium uppercase tracking-[-0.035em] text-[clamp(2.7rem,6vw,5.6rem)] text-stone-900 leading-[0.94]">
              <span className="block">{t("whyUs.title.line1")}</span>
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-[#A47C3B] via-[#a26850] to-[#6F5327]">
                {t("whyUs.title.line2")}
              </span>
            </h2>
          </div>

          <p className="lg:col-span-4 lg:pb-2 font-playfair text-lg sm:text-xl leading-relaxed text-stone-600">
            {t("whyUs.subtitle")}
          </p>
        </motion.header>

        {/* Three editorial commitments */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-10 lg:gap-16">
          {items.map((item, index) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.75,
                delay: index * 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              viewport={{ once: true, margin: "-60px" }}
              className="group relative py-2 md:px-4 lg:px-6"
            >
              <span className="block font-oswald font-light text-6xl sm:text-7xl leading-none tracking-[-0.04em] text-[#a26850]/20 transition-colors duration-500 group-hover:text-[#a26850]/45">
                {String(index + 1).padStart(2, "0")}
              </span>

              <h3 className="mt-8 md:min-h-[4rem] font-oswald font-normal uppercase tracking-[0.1em] text-xl sm:text-2xl text-stone-900 leading-tight transition-colors duration-500 group-hover:text-[#a26850]">
                {item.title}
              </h3>
              <p className="mt-6 font-playfair text-base sm:text-lg leading-relaxed text-stone-500">
                {item.text}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
